import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/dashboard.css';
import '../styles/resultpage.css';
import logo from '../assets/logo.png';
import wave from '../assets/result-hero3.png';
import ProfileDropdown from '../components/ProfileDropdown';
import PortalFaqHelp from '../components/PortalFaqHelp';
import ResultNotAvailableModal from '../components/ResultNotAvailableModal';
import API from '../api/api';

const readStorageJson = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key) || '{}');
  } catch {
    return {};
  }
};

const formatDuration = (seconds) => {
  const totalSeconds = Number(seconds);
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return '--';

  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = Math.floor(totalSeconds % 60);

  if (minutes === 0) return `${remainingSeconds} sec`;
  if (remainingSeconds === 0) return `${minutes} min`;
  return `${minutes} min ${remainingSeconds} sec`;
};

const formatQuestionCount = (answeredCount, totalQuestions) => {
  const answered = Number(answeredCount);
  const total = Number(totalQuestions);

  if (!Number.isFinite(answered)) return '--';
  if (Number.isFinite(total) && total > 0) return `${answered} of ${total}`;
  return `${answered} question${answered === 1 ? '' : 's'}`;
};

const formatSubmittedDate = (submittedAt) => {
  if (!submittedAt) return new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  const date = new Date(submittedAt);
  if (Number.isNaN(date.getTime())) return '--';

  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

export default function ResultsPage() {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [dbData, setDbData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewLoading, setViewLoading] = useState(false);

  const storedUser = readStorageJson('user');
  const storedResult = readStorageJson('result');

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const { data: resData } = await API.get('/exam/dashboard');
        if (resData && resData.exam) {
          setDbData(resData);
        }
      } catch (err) {
        console.error('Failed to fetch result status:', err);
      } finally {
        setLoading(false);
      }
    };
    checkStatus();
  }, []);

  const resultData = dbData?.exam?.result || storedResult || {};
  const data = {
    user: dbData?.user || storedUser.name || storedUser.email || 'Candidate',
    exam: { title: dbData?.exam?.title || storedResult.examTitle || localStorage.getItem('activeExamTitle') || 'Assessment' },
    result: {
      solvedIn: formatDuration(resultData.timeTaken),
      attemptedQuestions: formatQuestionCount(resultData.answeredCount, resultData.totalQuestions),
      submittedOn: formatSubmittedDate(resultData.submittedAt),
    },
  };

  const handleViewResult = async () => {
    setViewLoading(true);
    try {
      const { data: statusData } = await API.get('/drives/my-drive-status');
      if (statusData.resultPublished) {
        navigate(statusData.driveStatus === 'Shortlisted' ? '/congratulations' : '/keep-going');
        return;
      }
    } catch (err) {
      console.error('Status check failed', err);
    } finally {
      setViewLoading(false);
    }
    setShowModal(true);
  };

  const handleCheckResult = async () => {
    try {
      const { data: statusData } = await API.get('/drives/my-drive-status');
      if (statusData.resultPublished) {
        try {
          const { data: resData } = await API.get('/exam/dashboard');
          if (resData?.exam) setDbData(resData);
        } catch {}
        setShowModal(false);
      }
    } catch (err) {
      console.error('Result check failed', err);
    }
  };

  if (loading) {
    return <div className="dash-loading">Loading...</div>;
  }

  return (
    <div className="results-page">
      <nav className="dash-nav">
        <img src={logo} alt="Inteliment Logo" className="dash-logo" />
        <span className="dash-nav-title">Great Work, {data.user} !</span>
        <div className="dash-nav-right">
          <PortalFaqHelp />
          <ProfileDropdown user={data.user} email={storedUser.email} />
        </div>
      </nav>

      <div className="results-hero-bg">
        <div className="results-card-wrapper">
          <div className="results-card-glow"></div>
          <div className="results-card">
            <img src={wave} alt="" className="wave-img" />
            <div className="wave-overlay"></div>
            <div className="results-inner">
              <div className="results-heading-group">
                <h1 className="results-heading">The Wait is Over!</h1>
                <p className="results-subheading">{data.exam.title}</p>
              </div>

              <div className="results-stats">
                <div className="results-stat-item">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3D3D3D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                  <div className="results-stat-text">
                    <span className="results-stat-label">Solved In</span>
                    <span className="results-stat-value">{data.result.solvedIn}</span>
                  </div>
                </div>

                <div className="results-stat-item">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3D3D3D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                  </svg>
                  <div className="results-stat-text">
                    <span className="results-stat-label">Attempted Questions</span>
                    <span className="results-stat-value">{data.result.attemptedQuestions}</span>
                  </div>
                </div>

                <div className="results-stat-item">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3D3D3D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                  <div className="results-stat-text">
                    <span className="results-stat-label">Submitted on</span>
                    <span className="results-stat-value">{data.result.submittedOn}</span>
                  </div>
                </div>
              </div>

              <div className="result-banner">
                <p className="result-banner-text">
                  Your Responses Have Been Evaluated. Click Here To View Your Results!
                </p>
                <button className="view-result-btn" onClick={handleViewResult} disabled={viewLoading}>
                  {viewLoading ? 'Checking...' : 'View Result'}
                  {!viewLoading && (
                    <svg width="20.556" height="20.556" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12"/>
                      <polyline points="12 5 19 12 12 19"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <ResultNotAvailableModal
          onClose={() => setShowModal(false)}
          onCheckAgain={handleCheckResult}
        />
      )}
    </div>
  );
}